/**
 * Validation Utilities for PeopleTag
 * Functions for validating and checking data integrity
 */

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean} Whether email is valid
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @returns {boolean} Whether URL is valid
 */
export const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate wallet address (Ethereum format)
 * @param {string} address - Wallet address to validate
 * @returns {boolean} Whether address is valid
 */
export const isValidWalletAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Validate username
 * @param {string} username - Username to validate
 * @param {Object} options - Validation options
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validateUsername = (username, options = {}) => {
  const {
    minLength = 3,
    maxLength = 22,
    allowSpecialChars = true
  } = options;

  if (!username || typeof username !== 'string') {
    return { isValid: false, error: 'Username is required' };
  }

  const trimmed = username.trim();

  if (trimmed.length < minLength) {
    return { isValid: false, error: `Username must be at least ${minLength} characters` };
  }

  if (trimmed.length > maxLength) {
    return { isValid: false, error: `Username must be no more than ${maxLength} characters` };
  }

  const pattern = allowSpecialChars ? /^[a-zA-Z0-9_-]+$/ : /^[a-zA-Z0-9]+$/;
  if (!pattern.test(trimmed)) {
    return {
      isValid: false,
      error: allowSpecialChars
        ? 'Username can only contain letters, numbers, underscores, and hyphens'
        : 'Username can only contain letters and numbers'
    };
  }

  return { isValid: true, error: null };
};

/**
 * Validate tag
 * @param {string} tag - Tag to validate
 * @param {Object} options - Validation options
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validateTag = (tag, options = {}) => {
  const {
    minLength = 1,
    maxLength = 50,
    allowSpaces = true
  } = options;

  if (!tag || typeof tag !== 'string') {
    return { isValid: false, error: 'Tag is required' };
  }

  const trimmed = tag.trim();

  if (trimmed.length < minLength) {
    return { isValid: false, error: `Tag must be at least ${minLength} character${minLength !== 1 ? 's' : ''}` };
  }

  if (trimmed.length > maxLength) {
    return { isValid: false, error: `Tag must be no more than ${maxLength} characters` };
  }

  if (!allowSpaces && trimmed.includes(' ')) {
    return { isValid: false, error: 'Tag cannot contain spaces' };
  }

  return { isValid: true, error: null };
};

/**
 * Validate birth date
 * @param {string|Date} birthDate - Birth date to validate
 * @param {Object} options - Validation options
 * @returns {Object} { isValid: boolean, error: string|null, age: number|null }
 */
export const validateBirthDate = (birthDate, options = {}) => {
  const {
    minAge = 13,
    maxAge = 150
  } = options;

  if (!birthDate) {
    return { isValid: false, error: 'Birth date is required', age: null };
  }

  const date = birthDate instanceof Date ? birthDate : new Date(birthDate);
  
  if (isNaN(date.getTime())) {
    return { isValid: false, error: 'Invalid date format', age: null };
  }

  const today = new Date();
  const age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate()) ? age - 1 : age;

  if (actualAge < minAge) {
    return { isValid: false, error: `Must be at least ${minAge} years old`, age: actualAge };
  }

  if (actualAge > maxAge) {
    return { isValid: false, error: 'Invalid birth date', age: actualAge };
  }

  if (date > today) {
    return { isValid: false, error: 'Birth date cannot be in the future', age: null };
  }

  return { isValid: true, error: null, age: actualAge };
};

/**
 * Validate location coordinates
 * @param {Object} location - Location object with lat and lng
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validateLocation = (location) => {
  if (!location || typeof location !== 'object') {
    return { isValid: false, error: 'Location is required' };
  }

  const { lat, lng } = location;

  if (lat === undefined || lng === undefined) {
    return { isValid: false, error: 'Location must have both latitude and longitude' };
  }

  if (typeof lat !== 'number' || isNaN(lat) || lat < -90 || lat > 90) {
    return { isValid: false, error: 'Latitude must be a number between -90 and 90' };
  }

  if (typeof lng !== 'number' || isNaN(lng) || lng < -180 || lng > 180) {
    return { isValid: false, error: 'Longitude must be a number between -180 and 180' };
  }

  return { isValid: true, error: null };
};

/**
 * Validate social media URL
 * @param {string} url - URL to validate
 * @param {Array} allowedDomains - Optional array of allowed domains
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validateSocialMediaUrl = (url, allowedDomains = null) => {
  if (!url || typeof url !== 'string') {
    return { isValid: false, error: 'URL is required' };
  }

  if (!isValidUrl(url)) {
    return { isValid: false, error: 'Invalid URL format' };
  }

  if (allowedDomains && Array.isArray(allowedDomains) && allowedDomains.length > 0) {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      const isAllowed = allowedDomains.some(domain => 
        hostname === domain.toLowerCase() || hostname.endsWith('.' + domain.toLowerCase())
      );
      
      if (!isAllowed) {
        return { isValid: false, error: `URL must be from one of: ${allowedDomains.join(', ')}` };
      }
    } catch {
      return { isValid: false, error: 'Invalid URL format' };
    }
  }

  return { isValid: true, error: null };
};

/**
 * Sanitize string input (remove dangerous characters)
 * @param {string} input - Input to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string} Sanitized string
 */
export const sanitizeString = (input, options = {}) => {
  if (!input || typeof input !== 'string') return '';

  const {
    maxLength = 10000,
    allowHtml = false,
    trim = true
  } = options;

  let sanitized = input;

  if (trim) {
    sanitized = sanitized.trim();
  }

  if (!allowHtml) {
    // Remove HTML tags
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    // Escape HTML entities
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Check profile data health
 * @param {Object} profile - Profile to check
 * @returns {Object} Health check result
 */
export const checkProfileHealth = (profile) => {
  if (!profile) {
    return {
      healthy: false,
      issues: ['Profile data is missing'],
      score: 0
    };
  }

  const issues = [];
  let score = 100;

  // Check required fields
  if (!profile.username) {
    issues.push('Missing username');
    score -= 20;
  }

  // Check data types
  if (profile.tagsList && !Array.isArray(profile.tagsList)) {
    issues.push('tagsList must be an array');
    score -= 10;
  }

  if (profile.socialLinks && !Array.isArray(profile.socialLinks)) {
    issues.push('socialLinks must be an array');
    score -= 10;
  }

  // Check for empty arrays (not necessarily bad, but worth noting)
  if (profile.tagsList && profile.tagsList.length === 0) {
    issues.push('No tags added');
    score -= 5;
  }

  // Validate email if present
  if (profile.email && !isValidEmail(profile.email)) {
    issues.push('Invalid email format');
    score -= 5;
  }

  // Validate wallet address if present
  if (profile.walletAddress && !isValidWalletAddress(profile.walletAddress)) {
    issues.push('Invalid wallet address format');
    score -= 10;
  }

  // Validate location if present
  if (profile.location) {
    const locationValidation = validateLocation(profile.location);
    if (!locationValidation.isValid) {
      issues.push(`Location validation: ${locationValidation.error}`);
      score -= 5;
    }
  }

  // Validate birth date if present
  if (profile.birthDate) {
    const birthDateValidation = validateBirthDate(profile.birthDate);
    if (!birthDateValidation.isValid) {
      issues.push(`Birth date validation: ${birthDateValidation.error}`);
      score -= 5;
    }
  }

  // Validate social links
  if (profile.socialLinks && Array.isArray(profile.socialLinks)) {
    profile.socialLinks.forEach((link, index) => {
      if (link && !isValidUrl(link)) {
        issues.push(`Invalid social link at index ${index}`);
        score -= 2;
      }
    });
  }

  // Check for very long bio
  if (profile.bio && profile.bio.length > 5000) {
    issues.push('Bio is very long (may affect performance)');
    score -= 2;
  }

  return {
    healthy: issues.length === 0,
    issues,
    score: Math.max(0, score)
  };
};

/**
 * Validate array of tags
 * @param {Array} tags - Array of tags to validate
 * @param {Object} options - Validation options
 * @returns {Object} { isValid: boolean, errors: string[], validTags: string[] }
 */
export const validateTags = (tags, options = {}) => {
  const {
    maxTags = 50,
    minTagLength = 1,
    maxTagLength = 50
  } = options;

  if (!Array.isArray(tags)) {
    return {
      isValid: false,
      errors: ['Tags must be an array'],
      validTags: []
    };
  }

  const errors = [];
  const validTags = [];

  if (tags.length > maxTags) {
    errors.push(`Maximum ${maxTags} tags allowed`);
  }

  tags.forEach((tag, index) => {
    const validation = validateTag(tag, { minLength: minTagLength, maxLength: maxTagLength });
    if (validation.isValid) {
      validTags.push(tag.trim());
    } else {
      errors.push(`Tag ${index + 1}: ${validation.error}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    validTags
  };
};

/**
 * Normalize and deduplicate tags
 * @param {Array} tags - Array of tags
 * @returns {Array} Normalized and deduplicated tags
 */
export const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];

  return [...new Set(
    tags
      .filter(tag => tag && typeof tag === 'string')
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0)
  )];
};

